// Contact log and follow-ups (#3208): types, labels and small helpers shared by
// the property panel's log and the watchlist's "follow-ups due" list.

export type ContactKind = "call" | "voicemail" | "text" | "email" | "mail" | "visit" | "note";
export type ContactOutcome = "no_answer" | "spoke" | "interested" | "not_interested" | "wrong_number" | "callback";

export interface ContactEntry {
  id: string;
  propertyId: string;
  kind: ContactKind;
  outcome: ContactOutcome | null;
  note: string;
  /** YYYY-MM-DD, or null. */
  followUpOn: string | null;
  doneAt: string | null;
  createdAt: string;
}

export interface DueContact extends ContactEntry {
  /** Empty when the property is outside the viewer's plan. */
  address: string;
  city: string;
  countyId: string;
}

export const CONTACT_KINDS: { value: ContactKind; label: string }[] = [
  { value: "call", label: "Called" },
  { value: "voicemail", label: "Left voicemail" },
  { value: "text", label: "Texted" },
  { value: "email", label: "Emailed" },
  { value: "mail", label: "Mailed" },
  { value: "visit", label: "Visited" },
  { value: "note", label: "Note" },
];

export const CONTACT_OUTCOMES: { value: ContactOutcome; label: string }[] = [
  { value: "no_answer", label: "No answer" },
  { value: "spoke", label: "Spoke" },
  { value: "interested", label: "Interested" },
  { value: "not_interested", label: "Not interested" },
  { value: "wrong_number", label: "Wrong number" },
  { value: "callback", label: "Call back" },
];

export const contactKindLabel = (k: string): string => CONTACT_KINDS.find((x) => x.value === k)?.label ?? k;
export const contactOutcomeLabel = (o: string | null): string =>
  (o && CONTACT_OUTCOMES.find((x) => x.value === o)?.label) || "";

/** Today's date as YYYY-MM-DD in the user's own calendar. */
export function todayYmd(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/** Whole days from `today` to `ymd`: negative = overdue. */
export function daysFromToday(ymd: string, today: string = todayYmd()): number {
  const [y1, m1, d1] = today.split("-").map(Number);
  const [y2, m2, d2] = ymd.split("-").map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86_400_000);
}

/** "today", "tomorrow", "in 3 days", "2 days overdue". */
export function dueLabel(ymd: string, today: string = todayYmd()): string {
  const n = daysFromToday(ymd, today);
  if (n === 0) return "today";
  if (n === 1) return "tomorrow";
  if (n > 1) return `in ${n} days`;
  return `${-n} day${n === -1 ? "" : "s"} overdue`;
}
